import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { SystemAppNameModel } from "@api/types/systemappname";
import api from "@api";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { Field } from "../../../../../../components/form/fields/field";
import { useBusinessTypeSelect, useSystemCategorySelect } from "@hooks";
import { useSystemAppNameStore } from "../../../../../../store/hooksy";
import { Approver } from "./approver";
import { alertService } from "@services";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<SystemAppNameModel>;
}

type Key = keyof SystemAppNameModel;

export const SystemAppNameDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const [, setSytemAppNameStore] = useSystemAppNameStore();
  // custom hooks
  const {
    selectedBusinessType,
    businessTypes,
    isFetchingbusinessTypes,
    setSelectedBusinessType,
    handleBusinessTypeChange,
  } = useBusinessTypeSelect(form.values.businessType);
  const {
    selectedCategory,
    categories,
    isFetchingCategories,
    setSelectedCategory,
    handleCategoryChange,
  } = useSystemCategorySelect(
    form.values.systemCategory,
    selectedBusinessType,
    true
  );

  const [isSubmitting, setSubmitting] = useState(false);
  const [isBusinessTypeSet, setBusinessTypeState] = useState<boolean>(false);
  const [isSystemCategorySet, setSystemCategoryState] = useState<boolean>(
    false
  );

  let systemAppName: SystemAppNameModel = {
    id: 0,
    businessType: 0,
    systemCategory: 0,
    systemAppName: "",
    formName: "",
    thirdPartyFormUrl: "",
    accessDetailNote: "",
    approver: "",
    systemDesc: "",
    metadata: "",
    uamNote: "",
    assignmentTeam: "",
    isActive: false,
    isMultipleForm: false,
    isRevokeNeededForm: false,
    isSingleRequest: false,
    isRequireAttestation: false,
    isDataSensitive: false,
    isProvoRequest: false,
  };

  function clearAndCloseModel() {
    systemAppName.id = 0;
    systemAppName.businessType = 0;
    systemAppName.systemCategory = 0;
    systemAppName.systemAppName = "";
    systemAppName.formName = "";
    systemAppName.thirdPartyFormUrl = "";
    systemAppName.accessDetailNote = "";
    systemAppName.approver = "";
    systemAppName.systemDesc = "";
    systemAppName.metadata = "";
    systemAppName.uamNote = "";
    systemAppName.assignmentTeam = "";
    systemAppName.isActive = false;
    systemAppName.isMultipleForm = false;
    systemAppName.isRevokeNeededForm = false;
    systemAppName.isSingleRequest = false;
    systemAppName.isRequireAttestation = false;
    systemAppName.isDataSensitive = false;
    systemAppName.isProvoRequest = false;
    form.reset();
    onCancel();
  }

  useEffect(() => {
    if (form.values.businessType === 0) {
      setSelectedBusinessType(form.values.businessType);
    }
    if (form.values.systemCategory === 0) {
      setSelectedCategory(form.values.systemCategory);
    }
  }, [form.values.businessType, form.values.systemCategory]);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form, selectedCategory, selectedBusinessType]);

  function submitData() {
    systemAppName.id = form.values.id;
    systemAppName.businessType = selectedBusinessType;
    systemAppName.systemCategory = selectedCategory;
    systemAppName.systemAppName = form.values.systemAppName;
    systemAppName.formName = form.values.formName;
    systemAppName.thirdPartyFormUrl = form.values.thirdPartyFormUrl;
    systemAppName.accessDetailNote = form.values.accessDetailNote;
    systemAppName.approver = form.values.approver;
    systemAppName.systemDesc = form.values.systemDesc;
    systemAppName.metadata = form.values.metadata;
    systemAppName.uamNote = form.values.uamNote;
    systemAppName.assignmentTeam = form.values.assignmentTeam;
    systemAppName.isActive = form.values.isActive;
    systemAppName.isMultipleForm = form.values.isMultipleForm;
    systemAppName.isRevokeNeededForm = form.values.isRevokeNeededForm;
    systemAppName.isSingleRequest = form.values.isSingleRequest;
    systemAppName.isRequireAttestation = form.values.isRequireAttestation;
    systemAppName.isDataSensitive = form.values.isDataSensitive;
    systemAppName.isProvoRequest = form.values.isProvoRequest;
    setSubmitting(true);
    if (systemAppName.id !== 0) {
      // EDIT
      api.systemappname
        .putSystemAppName(systemAppName.id, systemAppName)
        .then((response) => {
          alertService.success({
            msg: "System/Application Name has been successfully updated.",
          });
          clearAndCloseModel();
          setSytemAppNameStore({ fetchList: true });
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      // CREATE
      api.systemappname
        .postSystemAppName(systemAppName)
        .then((response) => {
          alertService.success({
            msg: "System/Application Name has been successfully saved.",
          });
          clearAndCloseModel();
          setSubmitting(false);
          setSytemAppNameStore({ fetchList: true });
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "businessType":
        if (selectedBusinessType === 0) setBusinessTypeState(true);
        else setBusinessTypeState(false);

        return Promise.resolve([
          name as Key,
          Form.Validators.isNotEqual(selectedBusinessType, 0),
        ]);
      case "systemCategory":
        if (selectedCategory === 0) setSystemCategoryState(true);
        else setSystemCategoryState(false);
        if (selectedBusinessType && selectedCategory) {
          return Promise.resolve([name as Key]);
        } else {
          return Promise.resolve([
            name as Key,
            Form.Validators.isNotEqual(selectedCategory, 0),
          ]);
        }
      case "systemAppName":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "approver":
        if (value != null && value != "") {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value.toString().trim() as string),
          ]);
        }
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  let businessTypeMenu = null;
  if (isFetchingbusinessTypes) {
    businessTypeMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    businessTypeMenu = businessTypes.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  let categoriesMenu = null;
  if (isFetchingCategories) {
    categoriesMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    categoriesMenu = categories.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  let accessProvisioner = null;
  if (form.values.id) {
    accessProvisioner = <Approver systemAppId={form.values.id} />;
  }

  return (
    <Modal
      isOpen={open}
      title={`${modalTitle} System / Application Name`}
      closeModal={() => clearAndCloseModel()}
      size="md"
    >
      <Form>
        <Field label="Business Unit" size={6}>
          <Select
            value={selectedBusinessType}
            onChange={handleBusinessTypeChange}
            disabled={isFetchingbusinessTypes}
            required={true}
            name="businessType"
            error={isBusinessTypeSet}
          >
            {businessTypeMenu}
          </Select>
        </Field>
        <Field label="System Category" size={6}>
          <Select
            value={selectedCategory}
            onChange={handleCategoryChange}
            disabled={isFetchingCategories}
            required={true}
            name="systemCategory"
            error={isSystemCategorySet}
          >
            {categoriesMenu}
          </Select>
        </Field>
        <Form.Textbox
          label="System/Application Name"
          name="systemAppName"
          onChange={onChange}
          size={6}
          error={form.errors.systemAppName}
          value={form.values.systemAppName}
          required={true}
        />
        <Form.Textbox
          label="Form Name"
          name="formName"
          onChange={onChange}
          size={6}
          error={form.errors.formName}
          value={form.values.formName}
        />
        <Form.Textbox
          label="Third Party Form URL"
          name="thirdPartyFormUrl"
          onChange={onChange}
          size={6}
          error={form.errors.thirdPartyFormUrl}
          value={form.values.thirdPartyFormUrl}
        />
        <Form.Textbox
          label="Access Detail Note"
          name="accessDetailNote"
          onChange={onChange}
          size={6}
          error={form.errors.accessDetailNote}
          value={form.values.accessDetailNote}
        />
        <Form.Textbox
          label="Approver"
          name="approver"
          onChange={onChange}
          size={6}
          error={form.errors.approver}
          value={form.values.approver}
        />
        <Field label="" size={6}></Field>
        <Form.Textarea
          label="System Description"
          name="systemDesc"
          onChange={onChange}
          rows={4}
          rowsMax={5}
          size={4}
          placeholder="Write details here."
          error={form.errors.systemDesc}
          value={form.values.systemDesc}
        />
        <Form.Textarea
          label="Meta Data"
          name="metadata"
          onChange={onChange}
          rows={4}
          rowsMax={5}
          size={4}
          placeholder="Write details here."
          error={form.errors.metadata}
          value={form.values.metadata}
        />
        <Form.Textarea
          label="UAM Note"
          name="uamNote"
          onChange={onChange}
          rows={4}
          rowsMax={5}
          size={4}
          placeholder="Write details here."
          error={form.errors.uamNote}
          value={form.values.uamNote}
        />
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          size={12}
          value={form.values.isActive}
        />
        <Form.Checkbox
          label="Is Multiple Form"
          name="isMultipleForm"
          onChange={onChange}
          size={12}
          value={form.values.isMultipleForm}
        />
        <Form.Checkbox
          label="Is Revoke Needed Form"
          name="isRevokeNeededForm"
          onChange={onChange}
          size={12}
          value={form.values.isRevokeNeededForm}
        />
        <Form.Checkbox
          label="Is Single Request"
          name="isSingleRequest"
          onChange={onChange}
          size={12}
          value={form.values.isSingleRequest}
        />
        <Form.Checkbox
          label="Is Require Attestation"
          name="isRequireAttestation"
          onChange={onChange}
          size={12}
          value={form.values.isRequireAttestation}
        />
        <Form.Checkbox
          label="Is Data Sensitive"
          name="isDataSensitive"
          onChange={onChange}
          size={12}
          value={form.values.isDataSensitive}
        />
        <Form.Checkbox
          label="Is Provo Request"
          name="isProvoRequest"
          onChange={onChange}
          size={12}
          value={form.values.isProvoRequest}
        />
        <Form.Textbox
          label="Service Manager Queue"
          name="assignmentTeam"
          onChange={onChange}
          size={6}
          error={form.errors.assignmentTeam}
          value={form.values.assignmentTeam}
        />
      </Form>
      <br />
      {accessProvisioner}
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearAndCloseModel()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
